import React from 'react';
import './SubscriptionManageModal.css';
import { useTranslation } from 'react-i18next';

export default function SubscriptionManageModal({
  open,
  onClose,
  planName = 'Light',
  canCancelSubscription = true,
  currentAdminCount = 0,
  maxAdminCount = 0,
  adminCount = 0,
  currentEditorCount = 0,
  maxEditorCount = 0,
  currentProjectManagerCount = 0,
  maxProjectManagerCount = 0,
  editorCount = 0,
  projectManagerCount = 0,
  siteCount,
  activeSiteCount = siteCount,
  maxSiteCount = siteCount,
  unitPrice = '9,90',
  monthlyTotal,
  onDecrement,
  onIncrement,
  onAdminDecrement,
  onAdminIncrement,
  onEditorDecrement,
  onEditorIncrement,
  onProjectManagerDecrement,
  onProjectManagerIncrement,
  onUpdateSubscription,
  isUpdating = false,
  updateError = '',
  onRequestCancelSubscription,
}) {
  const { t } = useTranslation();
  const quantityToAdd = Math.max(0, Number(siteCount) - Number(maxSiteCount));
  const adminsToAdd = Math.max(0, Number(adminCount) - Number(maxAdminCount));
  const writersToAdd = Math.max(0, Number(editorCount) - Number(maxEditorCount));
  const managersToAdd = Math.max(0, Number(projectManagerCount) - Number(maxProjectManagerCount));
  const minimumSiteCount = Math.max(1, Number(activeSiteCount) || 1);
  const canDecreaseSiteCount = Number(siteCount) > minimumSiteCount;
  const canDecreaseAdminCount = Number(adminCount) > Number(currentAdminCount);
  const canDecreaseEditorCount = Number(editorCount) > Number(currentEditorCount);
  const canDecreaseProjectManagerCount =
    Number(projectManagerCount) > Number(currentProjectManagerCount);
  const adminLabel = currentAdminCount > 1 ? t('Administrateurs') : t('Administrateur');
  const hasPendingChanges = quantityToAdd > 0 || adminsToAdd > 0 || writersToAdd > 0 || managersToAdd > 0;
  const parsePrice = (value) => Number(String(value || 0).replace(',', '.')) || 0;
  const formatPrice = (value) => `${Number(value || 0).toFixed(2).replace('.', ',')}€`;
  const websiteSubtotal = Number(siteCount) * parsePrice(unitPrice);
  const adminSubtotal = adminsToAdd * 5.99;
  const writerSubtotal = writersToAdd * 4.99;
  const managerSubtotal = managersToAdd * 3.99;
  const computedMonthlyTotal = websiteSubtotal + adminSubtotal + writerSubtotal + managerSubtotal;

  if (!open) return null;

  return (
    <div className="smm-overlay" onClick={onClose}>
      <div
        className="smm-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Gérer votre abonnement"
      >
        <div className="smm-header">
          <div>
            <h2 className="smm-title">{t('Gérer votre abonnement')}</h2>
            <p className="smm-subtitle">
              {t('Ajustez vos ressources et licences utilisateurs')}
            </p>
          </div>
          <button
            type="button"
            className="smm-close"
            onClick={onClose}
            aria-label="Fermer la fenêtre"
          >
            ×
          </button>
        </div>

        <div className="smm-body">
          <div className="smm-plan-box">
            <div className="smm-plan-title-row">
              <span className="smm-plan-title">{t('Abonnement ')} {String(planName)}</span>
              <span className="smm-badge">{t('Forfait actuel')}</span>
            </div>
            <div className="smm-plan-meta">
              {activeSiteCount} {t('sites actifs')} · {maxSiteCount} {t('sites inclus')}
            </div>
            <div className="smm-plan-meta">
              {currentAdminCount} {adminLabel} · {maxAdminCount} {t('Administrateurs inclus')}
            </div>
          </div>

          <div className="smm-section">
            <div className="smm-section-title">{t('Ressources')}</div>
            <div className="smm-counters-group">
              <div className="smm-counter-row">
                <div className="smm-counter-label">
                  <div className="smm-counter-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </div>
                  <span>{t('Sites Web')}</span>
                </div>
                <div className="smm-counter-price">{unitPrice}€ / {t('site')}</div>
                <div className="smm-counter-controls">
                  <button
                    type="button"
                    onClick={onDecrement}
                    aria-label={t('Diminuer le nombre de sites')}
                    disabled={!canDecreaseSiteCount}
                  >
                    -
                  </button>
                  <span>{siteCount}</span>
                  <button
                    type="button"
                    onClick={onIncrement}
                    aria-label={t('Augmenter le nombre de sites')}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="smm-section">
            <div className="smm-section-title">{t('Utilisateurs additionnels')}</div>
            <div className="smm-counters-group">
              <div className="smm-counter-row">
                <div className="smm-counter-label">
                  <div className="smm-counter-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="8.5" cy="7" r="4" />
                      <polyline points="17 11 19 13 23 9" />
                    </svg>
                  </div>
                  <span>{t('Administrateur')}</span>
                </div>
                <div className="smm-counter-price">5,99€ / {t('mois')}</div>
                <div className="smm-counter-controls">
                  <button
                    type="button"
                    onClick={onAdminDecrement}
                    disabled={!canDecreaseAdminCount}
                    aria-label={t('Diminuer le nombre d’administrateurs')}
                  >
                    -
                  </button>
                  <span>{adminCount}</span>
                  <button
                    type="button"
                    onClick={onAdminIncrement}
                    aria-label={t('Augmenter le nombre d’administrateurs')}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="smm-counter-row">
                <div className="smm-counter-label">
                  <div className="smm-counter-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 19l7-7 3 3-7 7-3-3z" />
                      <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L11 18l1-1z" />
                      <path d="M2 2l5 5" />
                      <path d="M11 18l2 2" />
                    </svg>
                  </div>
                  <span>{t('Rédacteur')}</span>
                </div>
                <div className="smm-counter-price">4,99€ / {t('mois')}</div>
                <div className="smm-counter-controls">
                  <button
                    type="button"
                    onClick={onEditorDecrement}
                    disabled={!canDecreaseEditorCount}
                    aria-label={t('Diminuer le nombre de rédacteurs')}
                  >
                    -
                  </button>
                  <span>{editorCount}</span>
                  <button
                    type="button"
                    onClick={onEditorIncrement}
                    aria-label={t('Augmenter le nombre de rédacteurs')}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="smm-counter-row">
                <div className="smm-counter-label">
                  <div className="smm-counter-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <path d="M9 14l2 2 4-4" />
                    </svg>
                  </div>
                  <span>{t('Chef de projet')}</span>
                </div>
                <div className="smm-counter-price">3,99€ / {t('mois')}</div>
                <div className="smm-counter-controls">
                  <button
                    type="button"
                    onClick={onProjectManagerDecrement}
                    disabled={!canDecreaseProjectManagerCount}
                    aria-label={t('Diminuer le nombre de chefs de projet')}
                  >
                    -
                  </button>
                  <span>{projectManagerCount}</span>
                  <button
                    type="button"
                    onClick={onProjectManagerIncrement}
                    aria-label={t('Augmenter le nombre de chefs de projet')}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="smm-summary-box">
          <div className="smm-summary-total">
            <span className="smm-summary-amount">{formatPrice(computedMonthlyTotal)}</span>
            <span className="smm-summary-period">/ {t('mois')}</span>
          </div>

          <div className="smm-summary-line">
            <span>{siteCount} {t('Sites Web')}</span>
            <span>{formatPrice(websiteSubtotal)}</span>
          </div>
          {adminsToAdd > 0 && (
            <div className="smm-summary-line">
              <span>
                {adminsToAdd} {adminsToAdd > 1 ? t('Administrateurs') : t('Administrateur')}
              </span>
              <span>{formatPrice(adminSubtotal)}</span>
            </div>
          )}
          {writersToAdd > 0 && (
            <div className="smm-summary-line">
              <span>
                {writersToAdd} {t('Rédacteur')}
                {writersToAdd > 1 ? 's' : ''}
              </span>
              <span>{formatPrice(writerSubtotal)}</span>
            </div>
          )}
          {managersToAdd > 0 && (
            <div className="smm-summary-line">
              <span>
                {managersToAdd} {t('Chef de projet')}
                {managersToAdd > 1 ? 's' : ''}
              </span>
              <span>{formatPrice(managerSubtotal)}</span>
            </div>
          )}
          <div className="smm-summary-line smm-summary-line--strong">
            <span>{t('Total mensuel')}</span>
            <span>{formatPrice(computedMonthlyTotal)}</span>
          </div>

          <button
            type="button"
            className="smm-btn smm-btn--primary smm-btn--full"
            onClick={onUpdateSubscription}
            disabled={isUpdating || !hasPendingChanges}
          >
            {isUpdating ? t('Mise à jour...') : t('Mettre à jour mon abonnement')}
          </button>

          {updateError && <div className="smm-summary-error">{updateError}</div>}

          <div className="smm-summary-note">
            {t('Paiement sécurisé via Stripe. Annulable à tout moment.')}
          </div>

          {canCancelSubscription && (
            <button
              type="button"
              className="smm-cancel-title-btn"
              onClick={onRequestCancelSubscription}
            >
              {t('Résilier mon abonnement')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
